import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { invitesService } from '../services/invites'
import type { Invite } from '../types/invites'
import { CreateInviteModal } from './CreateInviteModal'

export function InvitesPage() {
  const { user } = useAuth()
  const [invitesSent, setInvitesSent] = useState<Invite[]>([])
  const [invitesReceived, setInvitesReceived] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)

  const hasInviterRole = user?.roles?.includes('inviter') || true

  useEffect(() => {
    loadInvites()
  }, [user])

  async function loadInvites() {
    if (!user) return

    try {
      setLoading(true)
      setError(null)

      // Load both sent and received invites in parallel
      const [sent, received] = await Promise.all([
        hasInviterRole
          ? invitesService.getInvitesSent(user.username)
          : Promise.resolve([]),
        invitesService.getInvitesReceived(user.username),
      ])

      setInvitesSent(sent)
      setInvitesReceived(received)
    } catch (err) {
      setError('Failed to load invites')
      console.error('Error loading invites:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateInvite(invitee: string) {
    try {
      await invitesService.createInvite(invitee)
      setShowModal(false)
      // Reload invites to show the new one
      loadInvites()
    } catch (err) {
      throw err // Let modal handle the error
    }
  }

  async function handleDeleteInvite(invitee: string) {
    try {
      await invitesService.deleteInvite(invitee)
      // Reload invites to reflect the deletion
      loadInvites()
    } catch (err) {
      setError('Failed to delete invite. It may have already been accepted.')
      console.error('Error deleting invite:', err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-discord-dark via-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-discord-dark via-gray-900 to-gray-800">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="text-4xl">🎮</div>
            <h1 className="text-2xl font-bold text-white">Invites</h1>
          </div>
          <a
            href="/"
            className="px-4 py-2 text-discord-gray hover:text-white transition-colors"
          >
            Back to Home
          </a>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {error && (
            <div className="bg-discord-red/20 border border-discord-red rounded-lg p-4">
              <p className="text-discord-red">{error}</p>
            </div>
          )}

          {/* Invites Sent Section (only for inviters) */}
          {hasInviterRole && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-white">
                  Invites Sent
                </h2>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 bg-discord-blurple hover:bg-discord-blurple/80 text-white rounded-lg transition-colors"
                >
                  + Send Invite
                </button>
              </div>

              {invitesSent.length === 0 ? (
                <p className="text-discord-gray">No invites sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {invitesSent.map((invite) => (
                    <div
                      key={`${invite.member}-${invite.created}`}
                      className="bg-white/5 rounded-lg p-4 flex justify-between items-center"
                    >
                      <div>
                        <p className="text-white font-medium">
                          {invite.invitee}
                        </p>
                        <p className="text-discord-gray text-sm">
                          {new Date(invite.created).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {invite.accepted ? (
                          <span className="text-discord-green">✓ Accepted</span>
                        ) : (
                          <>
                            <span className="text-discord-yellow">Pending</span>
                            <button
                              onClick={() => handleDeleteInvite(invite.invitee)}
                              className="px-3 py-1 text-sm text-discord-red hover:text-white hover:bg-discord-red/20 rounded transition-colors"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Invites Received Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-white mb-4">
              Invites Received
            </h2>

            {invitesReceived.length === 0 ? (
              <p className="text-discord-gray">No invites received yet.</p>
            ) : (
              <div className="space-y-2">
                {invitesReceived.map((invite) => (
                  <div
                    key={`${invite.member}-${invite.created}`}
                    className="bg-white/5 rounded-lg p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="text-white font-medium">
                        From: {invite.member}
                      </p>
                      <p className="text-discord-gray text-sm">
                        {new Date(invite.created).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      {invite.accepted ? (
                        <span className="text-discord-green">✓ Accepted</span>
                      ) : (
                        <span className="text-discord-yellow">Pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Invite Modal */}
      {showModal && (
        <CreateInviteModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateInvite}
        />
      )}
    </div>
  )
}
